import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core'
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { ApiService } from '../api.service'
import { firstValueFrom, Subscription } from 'rxjs'

@Component({
	selector: 'generate-course',
	templateUrl: './generate-course.component.html',
	styleUrl: './generate-course.component.scss'
})

export class GenerateCourseComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

	@Input() category: any = null
	@Input() dialog = false 
	
	unitsNum = 1
	courseFormGroup!: FormGroup
	courseFormError = false
	courseFormErrors: any[] = []

	// jobs: any[] = []
	message = ''
	progress = 0
	duration = 0
	durationStr = ''
	logs = ''
	state: 'pending' | 'running' | 'completed' | 'failed' = 'pending'
	result = ''

	generateBlankCourseSub = new Subscription
	jobAddSub = new Subscription
	jobStatusSub = new Subscription
	jobChainAddSub = new Subscription
	jobChainStatusSub = new Subscription

	constructor(private dataService: ApiService, private fb: FormBuilder) {}

	ngOnInit(): void {
		this.courseFormGroup = this.fb.group(
			{
				name: 		['', 	[Validators.required]],
				hours: 		[0, 	[Validators.required, Validators.min(1)]],
				unitsNum: 	[1, 	[Validators.required, Validators.min(1)]],
				units: 		this.fb.array<FormGroup>([])
			},
			{ validators: this.totalUnitHoursValidator() }
		)

		this.syncUnits()

		this.courseFormGroup.get('unitsNum')?.valueChanges.subscribe(value => {
			this.unitsNum = value
			this.syncUnits()
		})

		this.units.valueChanges.subscribe(() => {
			this.courseFormGroup?.updateValueAndValidity()
		})
	}

	ngAfterViewInit(): void {}

	ngOnChanges(): void {}

	ngOnDestroy(): void {
		this.generateBlankCourseSub?.unsubscribe()
		this.jobAddSub?.unsubscribe()
		this.jobStatusSub?.unsubscribe()
		this.jobChainAddSub?.unsubscribe()
		this.jobChainStatusSub?.unsubscribe()
	}

	get units(): FormArray<FormGroup> {
		return this.courseFormGroup?.get('units') as FormArray<FormGroup>
	}

	addUnit() {
		this.units.push(this.createUnitForm())
	}

	removeUnit(index: number) {
		this.units.removeAt(index)
	}

	syncUnits() {
		const current = this.units.length
		// console.log(this.unitsNum)
		// console.log(current)

		if (this.unitsNum > current) {
			for (let i = current; i < this.unitsNum; i++) {
				this.units.push(this.createUnitForm())
			}
		} else if (this.unitsNum < current) {
			for (let i = current; i > this.unitsNum; i--) {
				this.units.removeAt(i - 1)
			}
		}

		// console.log(this.units)
	}

	private createUnitForm(): FormGroup {
		return this.fb.group({
			name: 				['', 	[Validators.required]],
			hours: 				[0, 	[Validators.required, Validators.min(1)]],
			lessons: 			[0, 	[Validators.required, Validators.min(1)]],
			imagesSearch: 		[true, 	[Validators.required]],
			imagesPerHeading: 	[2, 	[Validators.required]],
			videosSearch: 		[true, 	[Validators.required]],
			videosPerLesson: 	[2, 	[Validators.required]],
			generateSummary: 	[true, 	[Validators.required]],
			generateGlossary: 	[true, 	[Validators.required]],
			criteriaText: 		['', 	[Validators.required]],
		})
	}

	private totalUnitHoursValidator(): ValidatorFn {
		return (form: AbstractControl): ValidationErrors | null => {
			const courseHours = +form.get('hours')?.value
			const units = form.get('units') as FormArray

			if (!courseHours || !units || units.length === 0) return null

			const unitHoursTotal = units.controls
			.map(control => Number(control.get('hours')?.value || 0))
			.reduce((acc, value) => acc + value, 0)

			return unitHoursTotal === courseHours ? null : { courseUnitHoursMismatch: true }
		}
	}

	checkErrors() {
		let errors: any[] = []

		let formTouched = this.courseFormGroup.touched
		if (!formTouched) {
			errors.push('No se ha introducido nada en el formulario')
			return errors
		}

		Object.keys(this.courseFormGroup.controls).forEach(controlName => {
			// console.log(controlName)
			let controlErrors = this.courseFormGroup.get(controlName)?.errors
			// console.log(controlErrors)

			if (controlName == 'name') {
				if (controlErrors?.required) errors.push('No se ha introducido un nombre para el curso.')
			}

			if (controlName == 'hours') {
				if (controlErrors?.min) errors.push('El número de horas del curso debe ser mayor a 0.')
			}

			if (controlName == 'unitsNum') {
				if (controlErrors?.min) errors.push('El número de unidades del curso debe ser mayor a 0.')
			}
		})
		
		this.units.controls.forEach((unit, unitIndex) => {
			// console.log(unit)
			// console.log(unitIndex)
			// console.log(unit.errors)
			let unitNum = unitIndex + 1

			Object.keys(unit.controls).forEach((unitControlName) => {
				// console.log(unitControlName)
				let unitControlErrors = unit.get(unitControlName)?.errors
				// console.log(unitControlErrors)

				if (unitControlName == 'name') {
					if (unitControlErrors?.required) errors.push(`Unidad ${unitNum}: No se ha introducido un nombre para la unidad.`)
				}

				if (unitControlName == 'hours') {
					if (unitControlErrors?.min) errors.push(`Unidad ${unitNum}: El número de horas del curso debe ser mayor a 0.`)
				}

				if (unitControlName == 'criteriaText') {
					if (unitControlErrors?.required) errors.push(`Unidad ${unitNum}: No se han introducido criterios de aprendizaje para la unidad.`)
				}
			})
		})

		let courseFormGroupErrors: any = this.courseFormGroup.errors
		if (courseFormGroupErrors) {
			if (courseFormGroupErrors?.courseUnitHoursMismatch) errors.push(`El número de horas totales de las unidades no coincide con el total de horas del curso.`)
		}

		// console.log(courseFormErrors)
		// console.log(unitErrors)
		// console.log(errors)

		return errors
	}

	async createBlankCourse(data: any) {
		let courseID = 0

		try {
			data = await firstValueFrom(
				this.dataService.generateBlankCourse(data)
			)
			courseID = +data 
			console.log(data)
			console.log(courseID)

			return courseID
		} catch (e) {
			console.log(e)
			this.state = 'failed'
			this.message = 'Error al crear el curso en blanco'
			throw e
		}
	}

	async generateCourse() {
		this.courseFormErrors = this.checkErrors()
		// console.log(this.courseFormErrors)
		if (this.courseFormErrors.length > 0) {
			this.courseFormError = true
			return
		}

		this.state = 'running'
		this.message = 'Iniciando...'

		let courseFormData: any = {
			'name': this.courseFormGroup?.get('name')?.value,
			'hours': this.courseFormGroup?.get('hours')?.value,
			'category': this.category.id,
			'translations': 'es',
		}

		console.log(courseFormData)

		let courseID = await this.createBlankCourse(courseFormData)
		console.log(courseID)
		if (courseID == 0 || courseID == null || courseID == undefined) {
			this.state = 'failed'
			this.message = 'Error al crear el curso en blanco'
			return
		}

		let command = 'unit_generate'
		let jobs: any[] = []
		this.units.controls.forEach((unit, u) => {
			jobs[u] = {
				'command': command,
				'data': {
					'unit_id': 0,
					'unit_name': unit.get('name')?.value,
					'course_id': courseID,
					'course_name': courseFormData.name,
					'hours': unit.get('hours')?.value,
					'lessons': unit.get('lessons')?.value,
					'images_search': unit.get('imagesSearch')?.value,
					'videos_search': unit.get('videosSearch')?.value,
					'images_per_heading': unit.get('imagesPerHeading')?.value,
					'videos_per_lesson': unit.get('videosPerLesson')?.value,
					'generate_summary': unit.get('generateSummary')?.value,
					'generate_glossary': unit.get('generateGlossary')?.value,
					'criteria_type': 'text',
					'criteria_text': unit.get('criteriaText')?.value,
				},
			}
		})

		if (jobs.length == 1) {
			console.log(jobs)

			let data = jobs[0].data
			this.jobAddSub = this.dataService.jobAdd(command, data)
			.subscribe({
				next: (jobId) => {
					console.log(jobId)

					let interval = setInterval(() => {
						this.jobStatusSub = this.dataService.jobStatus(jobId)
						.subscribe({
							next: (data) => {
								console.log(data)
								let status = data.status
								this.state = status

								this.message = data.message
								this.progress = data.progress
								this.duration = data.duration
								this.durationStr = new Date(data.duration * 1000).toISOString().slice(11, 19)

								this.logs = data.logs
								this.result = data.result

								if (status == 'completed' || status == 'failed') {
									clearInterval(interval)
								}
							},
							error: (e) => {
								console.log(e)
								clearInterval(interval)
								this.state = 'failed'
							}
						})
					}, 5000)
				},
				error: (e) => {
					console.log(e)
					this.state = 'failed'
				}
			})
		} else {
			this.jobChainAddSub = this.dataService.jobChainAdd(jobs)
			.subscribe({
				next: (jobChainId) => {
					console.log(jobChainId)
	
					let interval = setInterval(() => {
						this.jobChainStatusSub = this.dataService.jobChainStatus(jobChainId)
						.subscribe({
							next: (data) => {
								console.log(data)
								
								let status = data.status
								this.state = status
	
								this.message = data.message
								this.progress = data.progress
								this.duration = data.duration
								this.durationStr = new Date(data.duration * 1000).toISOString().slice(11, 19)
	
								this.logs = data.logs
								this.result = data.result
	
								if (status == 'completed' || status == 'failed') {
									clearInterval(interval)
								}
							},
							error: (e) => {
								console.log(e)
								clearInterval(interval)
								this.state = 'failed'
							}
						})
					}, 5000)
				},
				error: (e) => {
					console.log(e)
					this.state = 'failed'
				}
			})
		}
	}

}